import { formatHumanDate } from '@/lib/date-display.js';
import FactLine from './FactLine.jsx';
import OpenItem from './OpenItem.jsx';
import { IDENTITY_SOURCE_WORDS, NOT_RECORDED, word, refKey } from './words.js';

// THE AIRCRAFT on the account: registration, the identity facts and where
// they came from (the payload's identity_source, as a word), the link's
// source, retired and superseded as words. An AIRgus item naming an aircraft
// field sits on that row with the identity editor in place.

const ROWS = [['year', 'Year'], ['make', 'Make'], ['model', 'Model'], ['serial_number', 'Serial'], ['seats', 'Seats'], ['faa_unique_id', 'FAA id']];
const RENDERED = new Set(ROWS.map(([f]) => f));

export default function AircraftSection({ aircraft = [], byRef, ctx, onSaved, remedyHref }) {
  const itemsFor = (ac, field) => byRef.get(refKey({ subject: 'aircraft', id: ac.id, field })) || [];
  return (
    <section className="space-y-2" data-aircraft>
      <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Aircraft <span className="ml-1 font-mono normal-case tracking-normal">{aircraft.length}</span></h2>
      {aircraft.length === 0 ? <p className="text-xs italic text-text-dim">No aircraft on this account.</p> : null}
      {aircraft.map((ac) => {
        const other = [...byRef.entries()].filter(([k]) => k.startsWith(`aircraft:${ac.id}:`) && !RENDERED.has(k.split(':')[2])).flatMap(([, v]) => v);
        return (
          <article key={ac.id} className="rounded-card border border-border bg-surface" data-aircraft-id={ac.id}>
            <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
              <span className="font-mono text-[15px] font-semibold text-text">{ac.registration}</span>
              <span className="text-sm text-text-muted">{[ac.year, ac.make, ac.model].filter(Boolean).join(' ') || NOT_RECORDED}</span>
              {ac.retired_at ? <span className="text-xs text-text-dim" data-retired>retired {formatHumanDate(ac.retired_at)}{ac.retired_reason ? `, ${ac.retired_reason}` : ''}</span> : null}
              {ac.superseded_by_id ? <span className="text-xs text-text-dim">superseded</span> : null}
              {ac.link?.ended_at ? <span className="text-xs text-text-dim">link ended {formatHumanDate(ac.link.ended_at)}</span> : null}
              <span className="ml-auto text-xs text-text-dim" data-identity-source>{word(IDENTITY_SOURCE_WORDS, ac.identity_source)}</span>
            </header>
            <div className="px-4">
              {ROWS.map(([f, l]) => (
                <FactLine key={f} label={l} mono={f !== 'make' && f !== 'model'} fact_ref={{ subject: 'aircraft', id: ac.id, field: f }} items={itemsFor(ac, f)} ctx={ctx} onSaved={onSaved} remedyHref={remedyHref} value={ac[f]} />
              ))}
              <FactLine label="Linked by" value={ac.link?.source ? String(ac.link.source).replace(/_/g, ' ') : null} />
              {other.length ? (
                <div className="border-t border-border py-1" data-aircraft-other-items>
                  {other.map((it, i) => <OpenItem key={it.code + i} item={it} compact href={remedyHref?.(it)} />)}
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </section>
  );
}
